import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./components/Login";
import Layout from "./components/Layout";
import Register from "./components/Cashier/Register";
import CustomerDisplay from "./components/Cashier/CustomerDisplay";
import ProductList from "./components/Products/ProductList";
import ProductForm from "./components/Products/ProductForm";
import VendorList from "./components/Vendors/VendorList";
import InventoryView from "./components/Inventory/InventoryView";
import PricingMatrix from "./components/Pricing/PricingMatrix";
import ShelfTags from "./components/ShelfTags/ShelfTags";
import Reports from "./components/Reports/Reports";
import DetailedSales from "./components/Reports/DetailedSales";
import KegDashboard from "./components/Kegs/KegDashboard";
import QuickButtonManager from "./components/QuickButtons/QuickButtonManager";
import PettyCashManager from "./components/PettyCash/PettyCashManager";
import ShiftChecklists from "./components/Checklists/ShiftChecklists";
import TapMenuBoard from "./components/TapMenu/TapMenuBoard";
import DrawerHistory from "./components/DrawerHistory/DrawerHistory";
import StoreTasks from "./components/StoreTasks/StoreTasks";
import Returns from "./components/Returns/Returns";
import Dashboard from "./components/Dashboard/Dashboard";
import CalendarDashboard from "./components/Calendar/CalendarDashboard";
// QBO removed — replaced by Cheddar (future integration)
// import QuickBooksSettings from "./components/QuickBooks/QuickBooksSettings";
import MaintenancePage from "./components/Maintenance/MaintenancePage";
import LoyaltyPage from "./components/Loyalty/LoyaltyPage";
import GiftCardPage from "./components/GiftCards/GiftCardPage";
import BudgetPage from "./components/Budget/BudgetPage";
import SalesTaxPage from "./components/SalesTax/SalesTaxPage";
import ReportImportsPage from "./components/Imports/ReportImportsPage";
import SchedulingPage from "./components/Scheduling/SchedulingPage";
import OrderQueuePage from "./components/OrderQueue/OrderQueuePage";
import CustomerOrderPage from "./components/OrderQueue/CustomerOrderPage";
import EventsPage from "./components/Events/EventsPage";
import EventInquiryPage from "./components/Events/EventInquiryPage";
import AIInsightsPage from "./components/AI/AIInsightsPage";
import CalendarOCRPage from "./components/AI/CalendarOCRPage";
import AskKarenPage from "./components/AI/AskKarenPage";
import EmployeePage from "./components/Employees/EmployeePage";
import MobileScanCount from "./components/Inventory/MobileScanCount";
import InventoryCountPage from "./components/Inventory/InventoryCountPage";
import UpcCatchUpPage from "./components/Readiness/UpcCatchUpPage";
import PurchaseOrdersPage from "./components/PurchaseOrders/PurchaseOrdersPage";
import MySchedulePage from "./components/Staff/MySchedulePage";
import CashPayrollPage from "./components/CashPayroll/CashPayrollPage";
import OwnerLedgerPage from "./components/Owner/OwnerLedgerPage";
import "./index.css";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  return <>{children}</>;
}

// Owner only (bank, owner ledger) — Paula 9/10: "Patrick cannot see bank; Paula sees everything".
function OwnerRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.role !== "owner") return <Navigate to="/" />;
  return <>{children}</>;
}

// Owner/manager only — employees get redirected to their schedule page.
function ManagerRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.role !== "owner" && user?.role !== "manager") return <Navigate to="/my" />;
  return <>{children}</>;
}

function AppRoutes() {
  const { isAuthenticated, user } = useAuth();
  // Employees (cashier role) land on their schedule; owners/managers on the
  // dashboard (full sidebar — the register is one click away and the store
  // still rings on mPower until cutover).
  const homePath = user?.role === "owner" || user?.role === "manager" ? "/dashboard" : "/my";

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to={homePath} /> : <Login />} />
      <Route path="/my" element={
        <ProtectedRoute><MySchedulePage /></ProtectedRoute>
      } />
      <Route path="/customer-display" element={<CustomerDisplay />} />
      <Route path="/tap-menu" element={<TapMenuBoard />} />
      <Route path="/order" element={<CustomerOrderPage />} />
      <Route path="/event-inquiry" element={<EventInquiryPage />} />
      <Route path="/privacy" element={
        <div className="max-w-2xl mx-auto px-8 py-16">
          <h1 className="text-2xl font-bold mb-4" style={{ color: "var(--rbw-navy)" }}>Privacy Policy</h1>
          <p className="mb-3">Rieker Bottle Works (&quot;RBW&quot;) operates this platform for internal business use only.</p>
          <p className="mb-3">We collect only the information necessary to operate our point of sale, inventory, and accounting systems. This includes employee login credentials, sales transaction data, and vendor information.</p>
          <p className="mb-3">We do not sell, share, or distribute any personal or business data to third parties.</p>
          <p className="mb-3">All data is stored securely and access is restricted to authorized RBW employees.</p>
          <p className="mb-3">Contact: Rieker Bottle Works, Lancaster, PA</p>
          <p className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>Last updated: May 2026</p>
        </div>
      } />
      <Route path="/eula" element={
        <div className="max-w-2xl mx-auto px-8 py-16">
          <h1 className="text-2xl font-bold mb-4" style={{ color: "var(--rbw-navy)" }}>End User License Agreement</h1>
          <p className="mb-3">This software platform is developed for and used exclusively by Rieker Bottle Works, Lancaster, PA.</p>
          <p className="mb-3">Access is granted only to authorized employees of Rieker Bottle Works. Use of this system constitutes agreement to use it solely for legitimate business purposes.</p>
          <p className="mb-3">No third-party accounting integrations are active. Future integrations will be documented here.</p>
          <p className="mb-3">This is not a publicly distributed application. It is a private internal business tool.</p>
          <p className="mb-3">Contact: Rieker Bottle Works, Lancaster, PA</p>
          <p className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>Last updated: May 2026</p>
        </div>
      } />

      {/* Mobile scan & count - full screen, no layout nav */}
      <Route path="/scan-count" element={
        <ProtectedRoute><MobileScanCount /></ProtectedRoute>
      } />

      {/* Register - full screen, no layout nav */}
      <Route path="/register" element={
        <ProtectedRoute><Register /></ProtectedRoute>
      } />

      {/* Dashboard */}
      <Route path="/dashboard" element={
        <ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>
      } />

      {/* Calendar */}
      <Route path="/calendar" element={
        <ProtectedRoute><Layout><CalendarDashboard /></Layout></ProtectedRoute>
      } />

      {/* Manager/Admin pages with layout */}
      <Route path="/products" element={
        <ProtectedRoute><Layout><ProductList /></Layout></ProtectedRoute>
      } />
      <Route path="/products/new" element={
        <ProtectedRoute><Layout><ProductForm /></Layout></ProtectedRoute>
      } />
      <Route path="/products/:id" element={
        <ProtectedRoute><Layout><ProductForm /></Layout></ProtectedRoute>
      } />
      <Route path="/vendors" element={
        <ProtectedRoute><Layout><VendorList /></Layout></ProtectedRoute>
      } />
      <Route path="/orders" element={<Navigate to="/purchase-orders" />} />
      <Route path="/orders/:id" element={<Navigate to="/purchase-orders" />} />
      <Route path="/purchase-orders" element={
        <ProtectedRoute><Layout><PurchaseOrdersPage /></Layout></ProtectedRoute>
      } />
      <Route path="/inventory" element={
        <ProtectedRoute><Layout><InventoryView /></Layout></ProtectedRoute>
      } />
      <Route path="/upc-catchup" element={
        <ProtectedRoute><Layout><UpcCatchUpPage /></Layout></ProtectedRoute>
      } />
      <Route path="/inventory-counts" element={
        <ProtectedRoute><Layout><InventoryCountPage /></Layout></ProtectedRoute>
      } />
      <Route path="/pricing" element={
        <ManagerRoute><Layout><PricingMatrix /></Layout></ManagerRoute>
      } />
      <Route path="/shelf-tags" element={
        <ProtectedRoute><Layout><ShelfTags /></Layout></ProtectedRoute>
      } />
      <Route path="/kegs" element={
        <ProtectedRoute><Layout><KegDashboard /></Layout></ProtectedRoute>
      } />
      <Route path="/quick-buttons" element={
        <ProtectedRoute><Layout><QuickButtonManager /></Layout></ProtectedRoute>
      } />
      <Route path="/drawer-history" element={
        <ProtectedRoute><Layout><DrawerHistory /></Layout></ProtectedRoute>
      } />
      <Route path="/petty-cash" element={
        <ManagerRoute><Layout><PettyCashManager /></Layout></ManagerRoute>
      } />
      <Route path="/reports" element={
        <ManagerRoute><Layout><Reports /></Layout></ManagerRoute>
      } />
      <Route path="/reports/detailed" element={
        <ManagerRoute><Layout><DetailedSales /></Layout></ManagerRoute>
      } />
      <Route path="/needs-done" element={
        <ProtectedRoute><Layout><StoreTasks /></Layout></ProtectedRoute>
      } />
      <Route path="/checklists" element={
        <ProtectedRoute><Layout><ShiftChecklists /></Layout></ProtectedRoute>
      } />
      <Route path="/returns" element={
        <ProtectedRoute><Layout><Returns /></Layout></ProtectedRoute>
      } />
      {/* QBO removed — replaced by Cheddar */}
      <Route path="/maintenance" element={
        <ProtectedRoute><Layout><MaintenancePage /></Layout></ProtectedRoute>
      } />
      <Route path="/loyalty" element={
        <ProtectedRoute><Layout><LoyaltyPage /></Layout></ProtectedRoute>
      } />
      <Route path="/gift-cards" element={
        <ProtectedRoute><Layout><GiftCardPage /></Layout></ProtectedRoute>
      } />
      <Route path="/budget" element={
        <ManagerRoute><Layout><BudgetPage /></Layout></ManagerRoute>
      } />
      <Route path="/sales-tax" element={
        <ManagerRoute><Layout><SalesTaxPage /></Layout></ManagerRoute>
      } />
      <Route path="/report-imports" element={
        <ManagerRoute><Layout><ReportImportsPage /></Layout></ManagerRoute>
      } />
      <Route path="/scheduling" element={
        <ProtectedRoute><Layout><SchedulingPage /></Layout></ProtectedRoute>
      } />
      <Route path="/payroll" element={
        <ManagerRoute><Layout><CashPayrollPage /></Layout></ManagerRoute>
      } />
      <Route path="/owner-ledger" element={
        <OwnerRoute><Layout><OwnerLedgerPage /></Layout></OwnerRoute>
      } />
      <Route path="/cash-payroll" element={<Navigate to="/payroll" />} />
      <Route path="/employees" element={
        <ManagerRoute><Layout><EmployeePage /></Layout></ManagerRoute>
      } />
      <Route path="/order-queue" element={
        <ProtectedRoute><Layout><OrderQueuePage /></Layout></ProtectedRoute>
      } />
      <Route path="/events" element={
        <ProtectedRoute><Layout><EventsPage /></Layout></ProtectedRoute>
      } />
      <Route path="/ai-insights" element={
        <ProtectedRoute><Layout><AIInsightsPage /></Layout></ProtectedRoute>
      } />
      <Route path="/calendar-ocr" element={
        <ProtectedRoute><Layout><CalendarOCRPage /></Layout></ProtectedRoute>
      } />
      <Route path="/ask-karen" element={
        <ProtectedRoute><Layout><AskKarenPage /></Layout></ProtectedRoute>
      } />

      <Route path="/" element={<Navigate to="/dashboard" />} />
      <Route path="*" element={<Navigate to="/register" />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
